import type { FC } from "react";
import { useSelector } from "react-redux";

import { ActionButton, ActionButtonIconPlacement, ActionButtonIntent, Icons, Tag, TagIntent } from "widgets";

import { Tile } from "@/modules/core/components/Tile.js";
import { DATA_VIEWER_MODE } from "@/settings/settings.js";
import { pluralizeWord } from "../../app-utils/string.js";
import type { ChartLayoutState, ColumnAnnotation, DataSeries, Dataset } from "../../graphql.js";
import {
  type IColorSettings,
  type IDatasetViewerState,
  type SelectedDataSeries,
  countNumberOfActiveFilters,
} from "../../redux/modules/dataset-viewer.js";
import type { IAppState } from "../../redux/reducer.js";
import { DatasetPicker } from "../genome-viewer/DatasetPicker.js";
import { ConditionsSelector } from "./ConditionSelector.js";
import { DataSeriesSelector } from "./DataSeriesSelector.js";

interface IChartSettingsProps {
  viewerState: IDatasetViewerState;
  defaultDataSeries: string[];
  globalContext: boolean;
  groupId: number;

  onColorSettingsChanged: (colorSettings: IColorSettings) => void;
  onSelectedConditionsChanged: (selectedAnnotations: ColumnAnnotation[]) => void;
  onSelectedDatasetChanged: (datasetId: number) => void;
  onSelectedDataSeriesChanged: (selectedDataSeries: SelectedDataSeries[]) => void;
  onChartLayoutStateChanged: (chartLayout: ChartLayoutState) => void;
  onSearchDataSeries: (searchTerm: string) => Promise<DataSeries[]>;
  onAddDataSeries: (dataSeriesNames: string) => void;
  onSplitByCondition: (dataSeries: SelectedDataSeries, splitCondition: ColumnAnnotation | null) => void;
  onFilterPanelToggle: () => void;
}

export enum ChartSettingsWizardStep {
  complete = 0,
  initializing = 0,
  dataSetMissing = 1,
  chartTypeMissing = 2,
  conditionMissing = 3,
  dataSeriesMissing = 4,
}

const ChartSettings: FC<IChartSettingsProps> = (props) => {
  const isSampleMode = props.viewerState.mode === DATA_VIEWER_MODE.GROUP;
  const viewer = props.viewerState;
  const showFilterPanel = useSelector<IAppState, boolean>((state) => state.datasetViewer.showFilterPanel);

  // The wizard tooltips depend on the completion of the form.
  const wizardStep: ChartSettingsWizardStep =
    viewer.isLoading || viewer.isLoadingDataSeries || viewer.showFilterPanel
      ? ChartSettingsWizardStep.initializing
      : !viewer.dataset?.name
        ? ChartSettingsWizardStep.dataSetMissing
        : !viewer.chartLayout.chartType
          ? ChartSettingsWizardStep.chartTypeMissing
          : !viewer.selectedAnnotations || viewer.selectedAnnotations.length === 0
            ? ChartSettingsWizardStep.conditionMissing
            : !viewer.selectedDataSeries || viewer.selectedDataSeries.length === 0
              ? ChartSettingsWizardStep.dataSeriesMissing
              : ChartSettingsWizardStep.complete;

  const numberOfFilters = countNumberOfActiveFilters(viewer.filteredOutAnnotations);

  return (
    <div className="chart-settings-panel">
      {!isSampleMode && (
        <DatasetPicker
          isLoading={viewer.isLoadingAvailableDatasets}
          label={"Experimental Dataset"}
          datasets={viewer.availableDatasets}
          selectedDatasetId={viewer.dataset?.id}
          handleSelectedDatasetChanged={(dataset: Dataset) => props.onSelectedDatasetChanged(dataset?.id)}
          showMissingDatasetTooltip={wizardStep === ChartSettingsWizardStep.dataSetMissing}
          globalContext={props.globalContext}
        />
      )}
      {isSampleMode && (
        <div className="chart-dataset-selector selector-component">
          <div className="form-group">
            <label className="control-label">Source data</label>
            <Tile
              title={`${props.viewerState.dataset.columns.length} ${pluralizeWord(
                "sample",
                props.viewerState.dataset.columns.length,
              )}`}
              icon={<img src={Icons.Tiles.SamplesStack} width={24} />}
              to={`/group/${props.groupId}`}
            />
          </div>
        </div>
      )}

      {viewer.dataset && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <ActionButton
              onPress={props.onFilterPanelToggle}
              icon={showFilterPanel ? "close" : "keyboard_double_arrow_right"}
              iconPlacement={ActionButtonIconPlacement.Right}
              intent={ActionButtonIntent.Secondary}
              tooltip={"Add or remove samples from the display using filters"}
            >
              <span style={{ display: "inline-flex", columnGap: 5 }}>
                Select Filters{" "}
                {numberOfFilters > 0 && <Tag label={String(numberOfFilters)} intentColor={TagIntent.Outlined} />}
              </span>
            </ActionButton>
          </div>

          <div style={{ marginTop: 20 }}>
            <ConditionsSelector
              label={"X-axis"}
              wizardStep={wizardStep}
              annotations={viewer.availableAnnotations}
              timeSeriesEnabled={viewer.chartLayout.timeSeries}
              selectedAnnotations={viewer.selectedAnnotations}
              onChange={props.onSelectedConditionsChanged}
              limit={3}
              limitReachedMessage={"Limit of 3 x-axis conditions reached"}
              addOptionMessage={"Add Condition"}
            />
          </div>

          <div style={{ marginTop: 20 }}>
            <DataSeriesSelector
              label={"Data series"}
              dataset={viewer.dataset}
              wizardStep={wizardStep}
              selectedDataSeries={viewer.selectedDataSeries}
              isLoadingDataSeries={viewer.isLoadingDataSeries}
              dataSeriesNames={viewer.searchedDataSeriesNames}
              colorSettings={viewer.colorSettings}
              availableSplitByConditions={viewer.availableAnnotations}
              selectedConditions={viewer.selectedAnnotations}
              handleSelectedDataSeriesChanged={props.onSelectedDataSeriesChanged}
              handleColorSettingsChanged={props.onColorSettingsChanged}
              handleSplitByCondition={props.onSplitByCondition}
              onSearch={props.onSearchDataSeries}
              onAdd={props.onAddDataSeries}
              isSampleMode={isSampleMode}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ChartSettings;
